import { useEffect, useMemo, useCallback } from 'react';
import {
    objectTypes,
    humanFields,
    carFields,
    animalFields,
    techniqueFields,
} from '@api/Devices.api';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import useDictionary from '@ui/hooks/useDictionary';
import moment from 'moment';
import { server } from '@core/interfaces/server';
import { sim } from '@core/interfaces/sim';
import { dns } from '@core/interfaces/dns';
import { alarm } from '@core/interfaces/alarm';
import parseNumber from '@utils/parseNumber';

export interface IRow {
    option: string;
    value: string;
}

const useDeviceInfo = (onClose: ICloseEvent['onClose']) => {
    const { devices, groups, user, download } = useStore();
    const key = useKeyDown();
    const getWord = useDictionary();

    const currentDevice = useMemo(() => {
        const imei = devices.infoDeviceImei;
        const [currentDevice] = devices.deviceList.filter(
            (d) => d.imei === imei,
        );
        return currentDevice;
    }, [devices.infoDeviceImei]);

    const headers = ['option', 'value'];
    const [group] = groups.groupsList.filter(
        ({ id }) => id === currentDevice.groupId,
    );
    const timezone = user.settings.timezone;
    const {
        settings,
        motionSensorModes,
        alarmActions,
        motionSensorAmpModes,
        smsFormats,
        mode,
    } = devices.infoDeviceSettings;
    const getBaseInfo = (): IRow[] => {
        const res = [
            {
                option: getWord('deviceName'),
                value: currentDevice.name,
            },
            {
                option: getWord('imei'),
                value: currentDevice.imei,
            },
            {
                option: getWord('group'),
                value: group?.name,
            },
            {
                option: getWord('deviceType'),
                value: currentDevice.deviceType,
            },
        ];
        const { type, additionalInfo } = currentDevice;
        if (Object.values(objectTypes).includes(type as objectTypes)) {
            res.push({
                option: getWord('objectType'),
                value: getWord(type),
            });
            let keys: string[] = [];
            if (type === objectTypes.human) {
                keys = Object.values(humanFields);
            } else if (type === objectTypes.car) {
                keys = Object.values(carFields);
            } else if (type === objectTypes.animal) {
                keys = Object.values(animalFields);
            } else if (type === objectTypes.technique) {
                keys = Object.values(techniqueFields);
            }
            keys.forEach((key) => {
                res.push({
                    option: getWord(key),
                    value: additionalInfo[key as keyof typeof additionalInfo]!,
                });
            });
        }
        return res;
    };
    const getModeInfo = (): IRow[] => {
        if (mode && settings) {
            const result = [
                {
                    option: getWord('operationMode'),
                    value: mode ? getWord(mode) : '',
                },
            ];
            switch (mode) {
                case 'tracker':
                    result.push({
                        option: getWord('coordinateTransmissionPeriodMin'),
                        value: `${settings?.coordTxPeriod ?? ''}`,
                    });
                    break;
                case 'periodic':
                    result.push(
                        {
                            option: getWord('periodicActiveTimeMin'),
                            value: `${settings?.periodicActiveTime ?? ''}`,
                        },
                        {
                            option: getWord('periodicSleepTimeMin'),
                            value: `${settings?.periodicSleepTime ?? ''}`,
                        },
                        {
                            option: getWord('periodicLocationPeriod'),
                            value: `${settings?.periodicLocationPeriod ?? ''}`,
                        },
                    );
                    break;
                case 'alarm':
                    const {
                        alarmState,
                        alarmAction,
                        wakeUpTime,
                        wakeUpActiveTime,
                    } = settings;
                    if (!alarmState) break;
                    const alarmKeys = Object.keys(alarmState) as alarm[];
                    const activeAlarms = alarmKeys.filter(
                        (key) => alarmState && alarmState[key],
                    );
                    const values = activeAlarms
                        .map((key) => ({
                            alarm: key,
                            state: alarmState && alarmState[key],
                            action: alarmAction && alarmAction[key],
                            wakeUpTime: wakeUpTime && wakeUpTime[key],
                            wakeUpActiveTime:
                                wakeUpActiveTime && wakeUpActiveTime[key],
                        }))
                        .map((val) => [
                            {
                                option: `${getWord('wakeUpTime')} (${getWord(
                                    val.alarm,
                                )})`,
                                value: moment()
                                    .set({ hour: 0, minute: 0 })
                                    .add(
                                        parseNumber(val.wakeUpTime, 0) +
                                            (timezone ?? 0),
                                        'm',
                                    )
                                    .format('HH:mm'),
                            },
                            {
                                option: `${getWord(
                                    'periodicActiveTime',
                                )} (${getWord(val.alarm)}), ${getWord('min')}`,
                                value: `${val.wakeUpActiveTime}`,
                            },
                            {
                                option: `${getWord('action')} (${getWord(
                                    val.alarm,
                                )})`,
                                value: getWord(
                                    `${
                                        alarmActions![
                                            val.action as keyof typeof alarmActions
                                        ]
                                    }`,
                                ),
                            },
                        ])
                        .reduce((acc, val) => [...acc, ...val], []);
                    result.push(
                        {
                            option: getWord('activeAlarms'),
                            value: activeAlarms
                                .map((a) => getWord(a))
                                .join(', '),
                        },
                        ...values,
                    );
                    break;
            }
            return result;
        } else return [];
    };
    const getMotionSensorInfo = (): IRow[] => {
        if (settings) {
            let motionSensorMode = 0;
            let modeName: string | void;
            const modeValue = settings?.motionSensorMode;
            if (
                typeof modeValue === 'string' ||
                typeof modeValue === 'number'
            ) {
                let modeIndex: number | null = null;
                if (typeof modeValue === 'string') {
                    const index = parseInt(modeValue);
                    if (!isNaN(index)) modeIndex = index;
                } else if (typeof modeValue === 'number') {
                    modeIndex = modeValue;
                }
                if (modeIndex !== null) {
                    const item = motionSensorModes![modeIndex];
                    if (item) {
                        modeName = item;
                        motionSensorMode = modeIndex;
                    }
                }
            }
            const result = [
                {
                    option: getWord('motionSensor'),
                    value: modeName ? getWord(modeName) : '',
                },
            ];
            if (motionSensorMode > 0) {
                const amp = settings.accelerometerAmp;
                let ampVal = null;
                if (typeof amp === 'number') ampVal = amp;
                else if (typeof amp === 'string') ampVal = parseInt(amp, 10);
                if (typeof ampVal === 'number') {
                    const vals = new Array(5)
                        .fill(0)
                        .map((v, i) => (i + 1) * 10);
                    if (vals.indexOf(ampVal) === -1) ampVal = null;
                }
                result.push(
                    {
                        option: getWord('accelerometerAmp'),
                        value: ampVal
                            ? getWord(
                                  motionSensorAmpModes![(60 - ampVal) / 10 - 1],
                              )
                            : '',
                    },
                    {
                        option: getWord('motionSensorIgnoreTime'),
                        value: moment()
                            .set({ hour: 0, minute: 0 })
                            .add(settings.ignoreTime, 'm')
                            .format('HH:mm'),
                    },
                );
            }
            if (motionSensorMode > 1) {
                result.push(
                    {
                        option: getWord('motionSensorPeriodStart'),
                        value: moment()
                            .set({ hour: 0, minute: 0 })
                            .add(
                                parseNumber(settings.periodStart) +
                                    (timezone ?? 0),
                                'm',
                            )
                            .format('HH:mm'),
                    },
                    {
                        option: getWord('motionSensorPeriodEnd'),
                        value: moment()
                            .set({ hour: 0, minute: 0 })
                            .add(
                                parseNumber(settings.periodEnd) +
                                    (timezone ?? 0),
                                'm',
                            )
                            .format('HH:mm'),
                    },
                );
            }
            return result;
        } else return [];
    };
    const getSmsInfo = (): IRow[] => {
        if (settings) {
            const smsState = settings?.smsState;
            const isSmsEnable = smsState === 1 || smsState === '1';
            let result = [
                {
                    option: getWord('mainSim'),
                    value: settings?.mainSim ? `SIM${settings.mainSim}` : '',
                },
                {
                    option: getWord('smsState'),
                    value: isSmsEnable
                        ? getWord('enabled1')
                        : getWord('disabled1'),
                },
            ];
            if (isSmsEnable) {
                result = [
                    ...result,
                    {
                        option: getWord('smsFormatSending'),
                        value: getWord(
                            `${
                                smsFormats![
                                    settings.smsFormat as keyof typeof smsFormats
                                ]
                            }`,
                        ),
                    },
                    {
                        option: getWord('phoneNumSim1'),
                        value: settings.phoneNumSim1 ?? '',
                    },
                    {
                        option: getWord('phoneNumSim2'),
                        value: settings.phoneNumSim2 ?? '',
                    },
                ];
            }
            return result;
        } else return [];
    };
    const getServersInfo = (): IRow[] => {
        if (settings) {
            const {
                serverState,
                serverProtocol,
                serverAddress,
                serverPort,
                serverPassword,
            } = settings;
            const serverKeys = (
                serverState ? Object.keys(serverState) : []
            ) as server[];
            const activeServers = serverKeys.filter((key) => serverState![key]);
            const values = activeServers
                .map((key) => ({
                    server: key,
                    state: serverState![key],
                    protocol: serverProtocol![key],
                    address: serverAddress![key],
                    port: serverPort![key],
                    password: serverPassword![key],
                }))
                .map((val) => [
                    {
                        option: `${getWord('protocol')} (${getWord(
                            val.server,
                        )})`,
                        value: val.protocol ?? '',
                    },
                    {
                        option: `${getWord('address')} (${getWord(
                            val.server,
                        )})`,
                        value: val.address ?? '',
                    },
                    {
                        option: `${getWord('port')} (${getWord(val.server)})`,
                        value: `${val.port ?? ''}`,
                    },
                    {
                        option: `${getWord('password')} (${getWord(
                            val.server,
                        )})`,
                        value: val.password ?? '',
                    },
                ])
                .reduce((acc, val) => [...acc, ...val], []);
            return [
                {
                    option: getWord('activeServers'),
                    value: activeServers.map((a) => getWord(a)).join(', '),
                },
                ...values,
            ];
        } else return [];
    };
    const getInternetInfo = (): IRow[] => {
        if (settings) {
            const { dnsAddress, simApn, simLogin, simPassword } = settings;
            const dnsKeys = (
                dnsAddress ? Object.keys(dnsAddress) : []
            ) as dns[];
            const dnsInfo = dnsKeys.map((dns) => ({
                option: `${getWord('dnsAddress')} №${dns.split('dns')[1]}`,
                value: dnsAddress![dns] ?? '',
            }));
            const simKeys = (simApn ? Object.keys(simApn) : []) as sim[];
            const simInfo = simKeys
                .map((sim) => {
                    const simNum = sim.split('sim')[1];
                    return [
                        {
                            option: `${getWord('simApn')}${simNum}`,
                            value: simApn![sim] ?? '',
                        },
                        {
                            option: `${getWord('simLogin')}${simNum}`,
                            value: simLogin![sim] ?? '',
                        },
                        {
                            option: `${getWord('simPassword')}${simNum}`,
                            value: simPassword![sim] ?? '',
                        },
                    ];
                })
                .reduce((acc, val) => [...acc, ...val], []);
            return [...dnsInfo, ...simInfo];
        } else return [];
    };
    const getTimeSyncInfo = (): IRow[] => {
        if (settings) {
            const {
                timeSyncSatsState,
                timeSyncGsmState,
                timeSyncNtpState,
                ntpAddress,
                ntpPort,
            } = settings;
            const isNotTimeSyncSats = timeSyncSatsState === 0;
            const isNotTimeSyncGsm = timeSyncGsmState === 0;
            const isNotTimeSyncNtp = timeSyncNtpState === 0;
            const result = [
                {
                    option: getWord('timeSyncSatsState'),
                    value: isNotTimeSyncSats
                        ? getWord('disabled1')
                        : getWord('enabled1'),
                },
                {
                    option: getWord('timeSyncGsmState'),
                    value: isNotTimeSyncGsm
                        ? getWord('disabled1')
                        : getWord('enabled1'),
                },
                {
                    option: getWord('timeSyncNtpState'),
                    value: isNotTimeSyncNtp
                        ? getWord('disabled1')
                        : getWord('enabled1'),
                },
            ];
            if (timeSyncSatsState && !isNotTimeSyncNtp) {
                result.push(
                    {
                        option: getWord('ntpAddress'),
                        value: ntpAddress ?? '',
                    },
                    {
                        option: getWord('ntpPort'),
                        value: `${ntpPort ?? ''}`,
                    },
                );
            }
            return result;
        } else return [];
    };
    const getGnssInfo = (): IRow[] => {
        if (settings) {
            const {
                gnssPowersave,
                gnssMinSearchTime,
                gnssMaxSearchTime,
                gnssRectificationTime,
            } = settings;
            const isGnssPowersave =
                gnssPowersave === 1 || gnssPowersave === '1';
            const result = [
                {
                    option: getWord('gnssPowersave'),
                    value: isGnssPowersave
                        ? getWord('enabled')
                        : getWord('disabled'),
                },
            ];
            if (isGnssPowersave) {
                result.push(
                    {
                        option: `${getWord(
                            'gnssMinSearchTimeInPowersave',
                        )}, ${getWord('s')}`,
                        value: `${gnssMinSearchTime}`,
                    },
                    {
                        option: `${getWord('gnssMaxSearchTime1')}, ${getWord(
                            'min',
                        )}`,
                        value: `${gnssMaxSearchTime}`,
                    },
                );
            }
            result.push({
                option: `${getWord('gnssRectificationTime')}, ${getWord('s')}`,
                value: `${gnssRectificationTime}`,
            });
            return result;
        } else return [];
    };
    const getUpdateServerInfo = (): IRow[] => {
        if (settings) {
            return [
                {
                    option: getWord('updateServerAddress'),
                    value: settings.ftpAddress ?? '',
                },
                {
                    option: getWord('updateServerPort'),
                    value: `${settings.ftpPort ?? ''}`,
                },
                {
                    option: getWord('updateServerLogin'),
                    value: settings.ftpLogin ?? '',
                },
                {
                    option: getWord('updateServerPassword'),
                    value: settings.ftpPassword ?? '',
                },
                {
                    option: getWord('updateServerDir'),
                    value: settings.ftpDirectory ?? '',
                },
                {
                    option: getWord('updateServerFile'),
                    value: settings.ftpFilename ?? '',
                },
            ];
        } else return [];
    };
    const getLedInfo = (): IRow[] => {
        if (settings) {
            let value: string | null = null;
            if (settings.ledState === 1 || settings.ledState === '1')
                value = getWord('enabled1');
            else if (settings.ledState === 0 || settings.ledState === '0')
                value = getWord('disabled1');
            return [
                {
                    option: getWord('ledIndication'),
                    value: value ?? '',
                },
            ];
        } else return [];
    };
    const baseInfo = getBaseInfo();
    const modeInfo = getModeInfo();
    const motionSensorInfo = getMotionSensorInfo();
    const smsInfo = getSmsInfo();
    const serversInfo = getServersInfo();
    const internetInfo = getInternetInfo();
    const timeSyncInfo = getTimeSyncInfo();
    const gnssInfo = getGnssInfo();
    const updateServerInfo = getUpdateServerInfo();
    const ledInfo = getLedInfo();
    const rows: IRow[] = [
        ...baseInfo,
        ...modeInfo,
        ...motionSensorInfo,
        ...smsInfo,
        ...serversInfo,
        ...internetInfo,
        ...timeSyncInfo,
        ...gnssInfo,
        ...updateServerInfo,
        ...ledInfo,
    ];

    const values = {
        headers,
        rows,
        imei: currentDevice.imei,
        name: currentDevice.name,
    };

    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
        }
    };
    const handleDownloadClick = useCallback(() => {
        const header = headers.map((val) => getWord(val));
        const data = rows.map(({ option, value }) => [option, value]);
        const filename = getWord('infoAboutDevice');
        download.downloadCustomData(header, data, filename);
    }, [rows, headers]);
    const handlers = {
        handleDownloadClick,
    };

    useEffect(() => {
        if (!devices.infoDeviceImei) {
            onClose();
        }
    }, [devices.infoDeviceImei]);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        values,
        handlers,
    };
};

export default useDeviceInfo;
